import { TypeChecker } from '@attain-apps/type-guards'

import * as models from '../../../../models'
import { Ref } from '../../ref'
import { Subquery } from '../../subquery'
import * as node from '../node'

import type { Edge } from './edge'

/**
 * Represents an {@link Edge} between {@link Node Nodes} where the two nodes
 * are associated as ancestor/descendant or through a reference attribute,
 * and exactly one of the two nodes is either the root node of the
 * {@link Graph} or is connected to the root node (directly or indirectly)
 */
export interface ForConnection extends ForConnection.Base {
  /**
   * An {@link models.Entity.Id} that both nodes of this edge have on their
   * records. For both nodes, this can be the ID of the node's entity, the
   * ID of an ancestor of the node's entity, or an ID from one of the
   * reference attributes on the node's entity.
   *
   * @example
   * TODO: put example for number of different cases (e.g. hierarchy, ref, inheritance etc...)
   */

  // Can be undefined when a node's source is based on date ranges,
  // as these nodes don't share a common entity with other nodes
  readonly commonEntity?: models.Entity.Id
}
export namespace ForConnection {
  export const isValidNode: TypeChecker<ForConnection['node']> =
    TypeChecker.Factory.lazy(() => node.ForRecords.isForRecords).usingAlias(
      "query.Graph.Edge.ForConnection['node']",
    )

  /**
   * Contains essential information for
   * {@link Graph.Edge.ForConnection}
   *
   * A complete {@link Graph.Edge.ForConnection} can be generated by
   * passing this type to {@link Graph.Edge.ForConnection.from}
   */
  export interface Base extends Edge {
    readonly node: node.ForRecords
    /**
     * @see {@link Subquery}
     */
    readonly ref?: Readonly<Ref.WithDefaults>
    /**
     * Indicates this connection is between two sibling subqueries where one
     * of the subqueries is {@link Subquery.using using} the other subquery
     */
    readonly isSibling: boolean
  }

  // TODO: Maybe theres a way to do this logic in `List.add` through some
  // configuration or constructor params, so that we don't need to repeat
  // the node.

  export function from(
    schema: models.Schema,
    _node: node.ForRecords,
    base: Base,
  ): ForConnection {
    let commonEntity: ForConnection['commonEntity']
    if (base.ref) {
      // If there is a reference, the referencee (i.e. the entity being
      // referenced) is presumed to be the entity that does not own the
      // reference attribute
      const referenceeEntity =
        models.ForRecord.Id.from(base.ref.attr) === _node.source.modelId
          ? base.node.source.modelId
          : _node.source.modelId
      // We have to make sure the referencee is in fact an entity. Only entities
      // can be referenced. Snapshots can have reference attributes, but they
      // cannot be target of reference attributes
      models.Entity.Id.assertIsId(referenceeEntity)
      // Common entity is always the referencee, since the referencee has its
      // own ID (primary key) and the referencer has the referencee's ID
      // (foreign key)
      commonEntity = referenceeEntity
    } else if (_node.source.modelId) {
      // If we don't have a reference attribute, we ensure that both nodes have
      // entities, and then try to determine which entity is the ancestor
      // between the two
      models.Entity.Id.assertIsId(_node.source.modelId)
      models.Entity.Id.assertIsId(base.node.source.modelId)
      commonEntity = schema.determineAncestor(
        _node.source.modelId,
        base.node.source.modelId,
      )
    }

    return {
      ...base,
      commonEntity,
    }
  }

  export function stringify(edge: ForConnection): string {
    return JSON.stringify({
      commonEntity: edge.commonEntity,
      isSibling: edge.isSibling.toString(),
      node: edge.node.id,
      ref: edge.ref && Ref.stringify(edge.ref),
    } satisfies globalThis.Record<keyof ForConnection, string | undefined>)
  }
}
